/**
 * Iter41 — FLAC scope indicator (sidebar / user menu).
 *
 * Persistent visibility into the user's restricted field scope, with a
 * clickable modal listing all hidden/read-only fields and explaining why.
 * Hidden for admins (they see everything).
 */
import { useState, useMemo } from "react";
import axios from "axios";
import { Eye, ShieldOff, Info, X } from "lucide-react";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "./ui/dialog";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "./ui/tabs";
import { useEffect } from "react";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

const ROLE_LABEL = {
  agent: "Agent",
  manager: "Manager",
  backoffice: "Backoffice",
  admin: "Admin",
};

export default function FlacScopeIndicator({ fieldAccess, compact = false }) {
  const [open, setOpen] = useState(false);
  const [catalog, setCatalog] = useState(null);

  // Lazy-fetch the field catalog when modal opens (for labels/categories/tech_names)
  useEffect(() => {
    if (!open || catalog) return;
    (async () => {
      try {
        const r = await axios.get(`${API_URL}/admin/field-access/catalog`);
        setCatalog(r.data);
      } catch {
        // catalog endpoint is admin+BO only; for managers/agents we fall back to bare keys
        setCatalog({ fields: [], roles: [], permissions: [] });
      }
    })();
  }, [open, catalog]);

  if (!fieldAccess || !fieldAccess.enforced) {
    // admin or scope still loading
    return null;
  }

  const hidden = fieldAccess.hidden_fields || [];
  const readOnly = fieldAccess.read_only_fields || [];
  const totalRestricted = hidden.length + readOnly.length;
  const role = fieldAccess.role;
  if (totalRestricted === 0) return null;

  return (
    <>
      <button
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        className={
          compact
            ? "inline-flex items-center gap-1 rounded border border-sky-300 bg-sky-50 px-1.5 py-0.5 text-[10px] font-medium text-sky-900 hover:bg-sky-100"
            : "inline-flex items-center gap-1.5 rounded-md border border-sky-300 bg-sky-50 px-2 py-1 text-xs font-medium text-sky-900 hover:bg-sky-100 transition-colors"
        }
        data-testid="flac-scope-indicator-btn"
        title="Vezi câmpurile restricționate pentru rolul tău"
      >
        <Eye className={compact ? "h-3 w-3" : "h-3.5 w-3.5"} />
        {!compact ? (
          <>
            {hidden.length > 0 && <>{hidden.length} hidden</>}
            {hidden.length > 0 && readOnly.length > 0 && " · "}
            {readOnly.length > 0 && <>{readOnly.length} read-only</>}
          </>
        ) : (
          <>{totalRestricted}</>
        )}
      </button>

      <FlacScopeModal
        open={open}
        onClose={() => setOpen(false)}
        role={role}
        hidden={hidden}
        readOnly={readOnly}
        catalog={catalog}
      />
    </>
  );
}

function FlacScopeModal({ open, onClose, role, hidden, readOnly, catalog }) {
  // Map field_key -> {label, category, tech_name, description}
  const lookup = useMemo(() => {
    const map = {};
    if (catalog && Array.isArray(catalog.fields)) {
      for (const f of catalog.fields) map[f.field_key] = f;
    }
    return map;
  }, [catalog]);

  const renderRow = (fk, perm) => {
    const meta = lookup[fk];
    return (
      <li
        key={`${perm}-${fk}`}
        className="flex items-start gap-3 border-b border-slate-100 px-3 py-2 last:border-b-0"
        data-testid={`flac-modal-row-${fk}`}
      >
        <Badge
          variant="outline"
          className={
            perm === "hidden"
              ? "border-slate-300 bg-slate-50 text-slate-700"
              : "border-sky-300 bg-sky-50 text-sky-900"
          }
        >
          {perm === "hidden" ? "Ascuns" : "Read-only"}
        </Badge>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="text-sm font-medium text-slate-900">
              {meta?.label || fk}
            </p>
            {meta?.category && (
              <Badge variant="outline" className="text-[10px]">
                {meta.category}
              </Badge>
            )}
          </div>
          {meta?.description && (
            <p className="mt-0.5 text-xs text-slate-600">{meta.description}</p>
          )}
          <p className="mt-0.5 font-mono text-[10px] text-slate-400">{fk}</p>
        </div>
      </li>
    );
  };

  return (
    <Dialog open={open} onOpenChange={(v) => v ? null : onClose()}>
      <DialogContent className="max-w-3xl" data-testid="flac-scope-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldOff className="h-5 w-5 text-sky-700" />
            Câmpuri restricționate pentru rolul tău
          </DialogTitle>
          <DialogDescription>
            Rol curent: <strong>{ROLE_LABEL[role] || role}</strong>.{" "}
            Sunt <strong>{hidden.length + readOnly.length}</strong> câmpuri restricționate
            ({hidden.length} ascunse, {readOnly.length} read-only). Această matrice
            este configurabilă de admin în <em>Administrare → Câmpuri & Acces</em>.
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue={hidden.length > 0 ? "hidden" : "read"} className="mt-2">
          <TabsList>
            <TabsTrigger value="hidden" disabled={hidden.length === 0} data-testid="flac-tab-hidden">
              Ascunse ({hidden.length})
            </TabsTrigger>
            <TabsTrigger value="read" disabled={readOnly.length === 0} data-testid="flac-tab-read">
              Read-only ({readOnly.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="hidden" className="mt-3">
            {hidden.length === 0 ? (
              <p className="text-sm text-slate-500 italic">Nu există câmpuri complet ascunse.</p>
            ) : (
              <div className="max-h-96 overflow-y-auto rounded-md border border-slate-200">
                <ul>
                  {hidden.map((fk) => renderRow(fk, "hidden"))}
                </ul>
              </div>
            )}
          </TabsContent>

          <TabsContent value="read" className="mt-3">
            {readOnly.length === 0 ? (
              <p className="text-sm text-slate-500 italic">Nu există câmpuri read-only.</p>
            ) : (
              <div className="max-h-96 overflow-y-auto rounded-md border border-slate-200">
                <ul>
                  {readOnly.map((fk) => renderRow(fk, "read"))}
                </ul>
              </div>
            )}
          </TabsContent>
        </Tabs>

        <div className="mt-4 flex items-start gap-2 rounded-md bg-slate-50 px-3 py-2 text-xs text-slate-600">
          <Info className="h-4 w-4 flex-shrink-0 mt-0.5 text-slate-400" />
          <p>
            <strong>De ce?</strong> CRM-ul folosește o matrice de acces pe câmpuri (FLAC)
            care decide ce vezi și ce poți edita pentru fiecare rol. Câmpurile ascunse
            nu apar deloc în payload-uri sau formulare. Câmpurile read-only se afișează
            dar nu pot fi modificate. Contactează administratorul pentru drepturi suplimentare.
          </p>
        </div>

        <div className="mt-3 flex justify-end">
          <Button variant="outline" onClick={onClose} data-testid="flac-modal-close">
            <X className="mr-1.5 h-4 w-4" />
            Închide
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
